import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw, Loader2, CheckCircle2, Building2, User, IdCard, FolderOpen } from "lucide-react";
import { useSuiteContext } from "./SuiteContextProvider";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";
import { useAuth } from "@/contexts/AuthContext";
import { PersonLinkDialog } from "@/foundation/identity";

/**
 * ContextSwitcher — Foundation Identity & Context Engine.
 *
 * SOLO CAMBIO CONTESTO OPERATIVO della Persona autenticata.
 *
 * Il popover mostra esclusivamente:
 *   1. Persona (Nome Cognome + Codice Fiscale)
 *   2. Assegnazione attiva (evidenziata)
 *   3. Altre assegnazioni disponibili
 *
 * NON contiene:
 *   - simulazione / impersonation
 *   - richiesta di motivo
 *   - banner giallo o dicitura "Stai operando come..."
 *   - avvio di simulazione
 *
 * Il cambio contesto e' immediato, senza motivo, senza audit dedicato di
 * impersonation. Viene registrato come normale `context.switch`.
 *
 * La simulazione (impersonation) e' una funzione SEPARATA disponibile solo
 * agli utenti autorizzati (super_admin) tramite <ImpersonationTool />.
 */
export function ContextSwitcher({ trigger }) {
  const {
    context, available, availableError, loadingAvailable,
    refetchAvailable, switchContext,
  } = useSuiteContext();
  const { user: authUser } = useAuth();

  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const assignments = available?.assignments || [];
  const identity = available?.identity || context?.identity;
  const activeId = context?.active_assignment?.id;
  const isMulti = assignments.length > 1;

  const doSwitch = useCallback(async (assignmentId, opts = {}) => {
    if (assignmentId === activeId) { setOpen(false); return; }
    try {
      const nextCtx = await switchContext({ assignment_id: assignmentId }, opts);
      toast.success("Contesto aggiornato", {
        description: (nextCtx?.breadcrumb || []).join(" · "),
      });
      setOpen(false);
    } catch (e) {
      if (e?.code === "UNSAVED_CHANGES") {
        setPending({ kind: "switch", assignmentId });
        setUnsavedOpen(true);
        return;
      }
      const detail = e?.response?.data?.detail || e?.message || "Errore";
      toast.error(`Cambio contesto non riuscito: ${detail}`);
    }
  }, [activeId, switchContext]);

  const onDiscardAndProceed = useCallback(async () => {
    setUnsavedOpen(false);
    if (!pending) return;
    if (pending.kind === "switch") await doSwitch(pending.assignmentId, { discard: true });
    setPending(null);
  }, [pending, doSwitch]);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm" data-testid="suite-context-switcher-trigger">
              Cambia contesto
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end" data-testid="suite-context-switcher">
          <div className="space-y-3 p-4">

            {/* IDENTITA' — Persona + Codice Fiscale */}
            {identity && (
              <div className="border-b pb-3" data-testid="ctx-identity">
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-suite-primary/10 text-suite-primary">
                    <User size={15} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-suite-neutral-800">
                      {identity.display_name}
                    </div>
                    {identity.tax_code ? (
                      <div className="text-[11px] leading-tight text-suite-neutral-500 flex items-center gap-1 mt-0.5">
                        <IdCard size={10} />
                        <span className="font-mono">{identity.tax_code}</span>
                      </div>
                    ) : (
                      <div className="mt-1 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px]" data-testid="ctx-cf-missing">
                        <div className="flex items-center gap-1 font-medium text-amber-800">
                          <AlertCircle size={11} /> Codice Fiscale da completare
                        </div>
                        <p className="mt-0.5 text-amber-700">
                          L'identita' della Persona non e' ancora agganciata a un Codice Fiscale.
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 h-7 border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
                          onClick={() => { setOpen(false); setLinkOpen(true); }}
                          data-testid="ctx-complete-cf-btn"
                        >
                          <IdCard className="mr-1 h-3 w-3" /> Completa anagrafica
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* CARICAMENTO / ERRORE */}
            {loadingAvailable && !availableError && (
              <div className="flex items-center gap-2 text-xs text-suite-neutral-500" data-testid="ctx-loading">
                <Loader2 size={12} className="animate-spin" /> Caricamento contesti…
              </div>
            )}
            {availableError && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800" data-testid="ctx-error">
                <AlertCircle size={13} className="mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium">Impossibile caricare i contesti</div>
                  <button onClick={refetchAvailable} className="mt-1 font-medium underline">Riprova</button>
                </div>
              </div>
            )}

            {/* ASSIGNMENT REALI */}
            {!loadingAvailable && !availableError && assignments.length > 0 && (
              <div>
                {!isMulti ? (
                  <div data-testid="ctx-single-assignment">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-suite-neutral-400">
                      Assegnazione attiva
                    </div>
                    <div className="rounded-md border border-suite-primary/30 bg-suite-primary/5 p-2.5">
                      <div className="text-sm font-semibold text-suite-neutral-800">
                        {assignments[0].role_display_name}
                      </div>
                      {assignments[0].company_name && (
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-suite-neutral-600">
                          <Building2 size={11} /> {assignments[0].company_name}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div data-testid="ctx-multi-assignments">
                    <div className="mb-1.5 flex items-center justify-between">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-suite-neutral-400">
                        Le tue assegnazioni ({assignments.length})
                      </div>
                      <Button variant="ghost" size="sm" onClick={refetchAvailable}
                                className="h-6 px-1.5 text-suite-neutral-400" title="Ricarica">
                        <RefreshCw size={11} />
                      </Button>
                    </div>
                    <ul className="space-y-1">
                      {assignments.map((a) => {
                        const isActive = a.id === activeId;
                        return (
                          <li key={a.id}>
                            <button
                              type="button"
                              onClick={() => doSwitch(a.id)}
                              data-testid={`ctx-assignment-${a.id}`}
                              className={
                                "flex w-full items-center gap-2 rounded-md border p-2.5 text-left transition-colors " +
                                (isActive
                                  ? "border-suite-primary/40 bg-suite-primary/5"
                                  : "border-suite-neutral-200 hover:border-suite-primary/30 hover:bg-suite-neutral-50")
                              }
                            >
                              <div className={
                                "mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border " +
                                (isActive ? "border-suite-primary bg-suite-primary text-white" : "border-suite-neutral-300")
                              }>
                                {isActive && <CheckCircle2 size={10} />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-suite-neutral-800">
                                  {a.role_display_name}
                                </div>
                                {a.company_name && (
                                  <div className="mt-0.5 flex items-center gap-1 text-xs text-suite-neutral-500">
                                    <Building2 size={10} /> {a.company_name}
                                  </div>
                                )}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* NESSUN CONTESTO */}
            {!loadingAvailable && !availableError && assignments.length === 0 && (
              <div className="rounded-md border border-suite-neutral-200 bg-suite-neutral-50 p-3 text-xs text-suite-neutral-600" data-testid="ctx-empty">
                Nessuna assegnazione trovata per questa Persona.
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <UnsavedChangesDialog
        open={unsavedOpen}
        onClose={() => { setUnsavedOpen(false); setPending(null); }}
        onDiscard={onDiscardAndProceed}
      />

      <PersonLinkDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        userId={authUser?.id}
        suggestedFirstName={(identity?.display_name || "").split(" ")[0] || ""}
        suggestedLastName={(identity?.display_name || "").split(" ").slice(1).join(" ") || ""}
        onLinked={() => { refetchAvailable(); }}
      />
    </>
  );
}
